import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Auth pages
import RoleSelect from './pages/auth/RoleSelect';
import ResetPassword from './pages/auth/ResetPassword';
import ConfirmEmail from './pages/auth/ConfirmEmail';
import RootRedirect from './components/RootRedirect';
import ClientLogin from './pages/client/Login';
import ClientSignup from './pages/client/Signup';
import ClientForgot from './pages/client/Forgot';
import ClientCompleteProfile from './pages/client/CompleteProfile';
import ExpertLogin from './pages/expert/Login';
import ExpertSignup from './pages/expert/Signup';
import ExpertForgot from './pages/expert/Forgot';
import ExpertCompleteProfile from './pages/expert/CompleteProfile';

// Client Portal pages
import ClientHome from './pages/client/Home';
import ClientProfile from './pages/client/Profile';
import ClientNotifications from './pages/client/Notifications';
import MyBookings from './pages/client/MyBookings';
import BookingDetail from './pages/client/BookingDetail';
import Favorites from './pages/client/Favorites';
import MyExperts from './pages/client/MyExperts';
import { ExpertPage } from './pages/client/ExpertPage';

// Expert Dashboard
import ExpertDashboard from './pages/expert/Dashboard';
import ExpertBookings from './pages/expert/Bookings';
import ExpertClients from './pages/expert/Clients';
import ExpertServices from './pages/expert/Services';
import ExpertSettings from './pages/expert/Settings';
import ExpertIntegrations from './pages/expert/Integrations';
import ExpertWorkingHours from './pages/expert/WorkingHours';
import GoogleCalendarCallback from './pages/expert/GoogleCalendarCallback';
import ClientDetail from './pages/expert/ClientDetail';

// Legal pages
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Old pages (keep for now, can be migrated later)
import Calendar from './pages/Calendar';
import PublicBooking from './pages/PublicBooking';

// Public pages
import PublicExpertPage from './pages/public/ExpertPage';
import LandingPage from './pages/public/LandingPage';

import './App.css';

// Get Google Client ID from environment
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <Router>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 4000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
        {/* Root - smart redirect based on auth state */}
        <Route path="/" element={<RootRedirect />} />

        {/* Auth routes */}
        <Route path="/auth/select" element={<RoleSelect />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />

        {/* Client auth routes */}
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/client/signup" element={<ClientSignup />} />
        <Route path="/client/forgot" element={<ClientForgot />} />
        <Route path="/client/complete-profile" element={<ClientCompleteProfile />} />

        {/* Client portal routes */}
        <Route path="/u" element={<ClientHome />} />
        <Route path="/u/bookings" element={<MyBookings />} />
        <Route path="/u/bookings/:id" element={<BookingDetail />} />
        <Route path="/u/favorites" element={<Favorites />} />
        <Route path="/u/my-experts" element={<MyExperts />} />
        <Route path="/u/profile" element={<ClientProfile />} />
        <Route path="/u/notifications" element={<ClientNotifications />} />
        <Route path="/u/experts/:expertSlug" element={<ExpertPage />} />

        {/* Expert routes */}
        <Route path="/expert/login" element={<ExpertLogin />} />
        <Route path="/expert/signup" element={<ExpertSignup />} />
        <Route path="/expert/forgot" element={<ExpertForgot />} />
        <Route path="/expert/complete-profile" element={<ExpertCompleteProfile />} />
        <Route path="/app/dashboard" element={<ExpertDashboard />} />
        <Route path="/app/bookings" element={<ExpertBookings />} />
        <Route path="/app/clients" element={<ExpertClients />} />
        <Route path="/app/clients/:id" element={<ClientDetail />} />
        <Route path="/app/services" element={<ExpertServices />} />
        <Route path="/app/settings" element={<ExpertSettings />} />
        <Route path="/app/integrations" element={<ExpertIntegrations />} />
        <Route path="/app/working-hours" element={<ExpertWorkingHours />} />
        <Route path="/app/integrations/google-calendar/callback" element={<GoogleCalendarCallback />} />

        {/* Landing page - always accessible */}
        <Route path="/home" element={<LandingPage />} />

        {/* Legal pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Public booking page */}
        <Route path="/book/:slug" element={<PublicExpertPage />} />

        {/* Legacy routes (keep for backward compatibility) */}
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/book" element={<PublicBooking />} />

        {/* Catch all - redirect to role select */}
        <Route path="*" element={<Navigate to="/auth/select" replace />} />
      </Routes>
    </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
